import { useQuery } from "@tanstack/react-query";
import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { filiaisApi } from "@/api/filiaisApi";
import { useSelectedFilial } from "@/context/SelectedFilialContext";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

export function FilialSelector() {
  const [open, setOpen] = useState(false);
  const { filial, setFilial } = useSelectedFilial();

  const { data, isLoading } = useQuery({
    queryKey: ["filiais", "selector"],
    queryFn: () => filiaisApi.listar({ ativo: true, limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  const filiais = data?.items ?? [];
  const label = filial ? filial.nome_fantasia || filial.codigo : "Todas as filiais";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="h-9 max-w-[220px] justify-between gap-2"
        >
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm">{label}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Buscar filial..." />
          <CommandList>
            <CommandEmpty>
              {isLoading ? "Carregando..." : "Nenhuma filial encontrada."}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__todas__"
                onSelect={() => {
                  setFilial(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn("mr-2 h-4 w-4", !filial ? "opacity-100" : "opacity-0")}
                />
                <span className="font-medium">Todas as filiais</span>
              </CommandItem>
            </CommandGroup>
            {filiais.length > 0 && (
              <CommandGroup heading="Filiais ativas">
                {filiais.map((f) => {
                  const codigo = f.codigo ?? "";
                  const nome = f.nome_fantasia ?? f.razao_social ?? "(sem nome)";
                  const selected = filial?.id === f.id;
                  return (
                    <CommandItem
                      key={f.id}
                      value={`${nome} ${codigo} ${f.razao_social ?? ""}`}
                      onSelect={() => {
                        setFilial({
                          id: f.id,
                          codigo: codigo,
                          nome_fantasia: nome,
                        });
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selected ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate text-sm font-medium">{nome}</span>
                        <span className="truncate text-xs text-muted-foreground">
                          {f.razao_social ?? "—"}
                          {codigo ? ` · #${codigo}` : ""}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
